/*
 * The AI Chats frontend module (modules.md §8, S13 §1). Everything the shell knows about the module is
 * in this object: its routes, its reducer for `aichat.*` events, what it puts into core's slots, how it
 * binds to the running client (`bind.ts`), its translation namespace and its slice's life on disk.
 * `web/src/modules.ts` is the only file that names it, and switching the module off is deleting that
 * one line.
 *
 * What this file may import is its budget discipline: types, the list facts of the slice, the reducer,
 * the key bindings and lazy components, and nothing else. It is statically reachable from `main.tsx`,
 * so a value import of a screen or of `./i18n/en.ts` — here or anywhere this file reaches — moves that
 * weight into the entry chunk (`core/modules/entryClosure.test.ts`, `scripts/checkBudgets.ts`).
 *
 * The rail's AI Chats row stands for a team member the module admits: the server sends the module's
 * bootstrap entry only to them, and `admits` answers from its presence (`sync/slice.ts`).
 */

import { createElement, lazy, Suspense } from "react"
import type { FrontendModule } from "../../core/modules/registry.ts"
import { aichatActivityRows } from "./activityRows.ts"
import { bindAichatModule } from "./bind.ts"
import { aichatChatLabels } from "./chatLabels.ts"
import { aichatQuickSwitcher } from "./home/quickSwitcher.ts"
import { hasWaiting } from "./home/waitingFacts.ts"
import { aichatDetailsViews } from "./panel/register.ts"
import { aichatReducer } from "./reducer.ts"
import { aichatRoutes } from "./routes.tsx"
import { SETTINGS_VIEW } from "./settings/panel.ts"
import { aichatShortcuts } from "./shortcuts.ts"
import { onAichatBootstrap } from "./sync/bootstrap.ts"
import {
	aichatAdmits,
	dehydrateAichatSlice,
	hydrateAichatSlice,
	type AichatSlice,
} from "./sync/slice.ts"

/*
 * The column beside the AI Chats place: the find field, "Waiting for you" and the reader's chats
 * (S13 §2.5). It claims the column on its place, and folds away below 1 600 px beside a chat and its
 * review (S12 §2). Lazy, because a sidebar section is rendered by a core frame from the first paint.
 */
const AiSidebar = lazy(async () => ({
	default: (await import("./home/AiSidebar.tsx")).AiSidebar,
}))

/** The width below which the column folds beside a chat or a review: below it the page needs the room. */
const LIST_FOLDS_BELOW_PX = 1600

/**
 * The addresses beside which the column folds below 1 600 px: a chat (S12 §2, whose band's first
 * control brings the column back over the page) and its review, whose diff needs the room. `/ai` and
 * `/ai/new` keep the column: they are the column's own place.
 */
const FOLDS_BESIDE = /^\/ai\/(?!new(?:\/|$))[^/]+/

/*
 * Home's "AI chats waiting for you" (S13 §2.7), above chat's catch-up. Lazy like every screen of the
 * module; whether it holds anything is asked from the slice alone (`hasWaiting`), so Home says "You’re
 * up to date" only when no AI chat waits for the reader either.
 */
const HomeWaiting = lazy(async () => ({
	default: (await import("./home/HomeSection.tsx")).HomeSection,
}))

/** Where Home's section stands: before chat's catch-up (order 10). */
const HOME_WAITING_ORDER = 5

/**
 * A chat's Settings panel (S13 §4): a view of the details column, a chunk of its own, opened by a
 * request naming the chat (`settings/panel.ts`).
 */
const SettingsView = lazy(async () => ({
	default: (await import("./settings/SettingsView.tsx")).SettingsView,
}))

/*
 * teamBase's pages for a preview's visitors (S10 §3–§4, S13 §7): full pages, drawn without the shell
 * or the socket and reachable signed out, in one chunk of their own (`preview/pages.ts`). Each is
 * addressed by the preview's label, `<key>-<name>`, the first label of its host. A static `preview`
 * segment outranks the chat's `/ai/$key` (`preview` has no digit, so it is never a key).
 */
const previewPages = () => import("./preview/pages.ts")
const PREVIEW_PAGES = [
	{ path: "/ai/preview/$label", load: () => previewPages().then((m) => m.VisitorPage) },
	{ path: "/ai/preview/$label/starting", load: () => previewPages().then((m) => m.StartingPage) },
	{
		path: "/ai/preview/$label/unreachable",
		load: () => previewPages().then((m) => m.UnreachablePage),
	},
]

/**
 * An AI chat is a page of its own: `aichat` is the page (the density, the details column),
 * `aichatLog` the transcript's row walk, and the message box sits under both — so an AI chat's chord
 * must stay clear of the box's and of the global ones, but may mean there what a conversation's
 * chord means in a conversation. The Code layer of a review (`diff`) is a page of its own again,
 * with nothing written in it but a filter.
 */
const AI_CHAT_STACK = ["global", "aichat", "aichatLog", "composer"]

/** Administration → AI Chats: every chat, the machine, and every member's Claude logins. */
const AdminPage = lazy(async () => ({
	default: (await import("./admin/AichatAdminPage.tsx")).AichatAdminPage,
}))

/** Administration → Open to AI chats: the AI chats part, the chats closed to the others. */
const AgentReachChats = lazy(() => import("./admin/AgentReachChats.tsx"))

/**
 * Settings → Claude logins (S17 §2): the member's own Claude accounts, which their requests run on.
 * A page of core's settings frame, at `/settings/claude-logins` — the address `routes/links.ts`'s
 * `AI_CLAUDE_LOGIN_HREF` names for every door to it (the home's link, the compose box's hint, the
 * reminders) — and a chunk of its own, like every screen of the module.
 */
const LoginsPage = lazy(async () => ({
	default: (await import("./logins/LoginsPage.tsx")).LoginsPage,
}))

export const aichat: FrontendModule = {
	id: "aichat",
	// Resolved at once, with each screen a `lazy()` of its own (./routes.tsx says why).
	routes: (parent) => Promise.resolve(aichatRoutes(parent)),
	reducer: aichatReducer,
	admits: aichatAdmits,
	activityRows: aichatActivityRows,
	activityGroups: { aichat: "aichat.activity.filter" },
	fullPages: PREVIEW_PAGES,
	bind: bindAichatModule,
	slots: {
		sidebarSections: [
			{
				id: "aichat.nav",
				titleKey: "aichat.place.title",
				order: 10,
				place: "ai",
				sidebar: "own",
				fold: { belowPx: LIST_FOLDS_BELOW_PX, on: (pathname) => FOLDS_BESIDE.test(pathname) },
				Rows: AiSidebar,
			},
		],
		homeSections: [
			{
				id: "aichat.waiting",
				order: HOME_WAITING_ORDER,
				render: () => createElement(Suspense, { fallback: null }, createElement(HomeWaiting)),
				hasItems: hasWaiting,
			},
		],
		// Claude's work beside a chat — Tasks · Workflow · Context (S12 §6.2) — and the chat's
		// Settings (S13 §4): lazy views behind ids, opened by a request naming the chat.
		detailsViews: [
			...aichatDetailsViews,
			{
				id: SETTINGS_VIEW,
				titleKey: "aichat.settings.title",
				group: SETTINGS_VIEW,
				View: SettingsView,
			},
		],
		adminPages: [{ path: "aichat", titleKey: "aichat.place.title", Page: AdminPage }],
		agentReach: [{ id: "aichat.chats", order: 30, Part: AgentReachChats }],
		settingsPages: [{ path: "claude-logins", titleKey: "aichat.logins.title", Page: LoginsPage }],
		workSubjects: [
			{ kind: "aichat.chat", labelKey: "aichat.admin.work.kind.chat" },
			{ kind: "aichat.auth", labelKey: "aichat.admin.work.kind.auth" },
		],
		quickSwitcherProviders: [aichatQuickSwitcher],
		// The chats a document's byline names to their readers: "Claude, asked by Ben in Pricing rework".
		chatLabels: [aichatChatLabels],
		shortcuts: aichatShortcuts,
		shortcutScopes: [
			{ scope: "aichat", titleKey: "aichat.shortcut.scope.page", stack: AI_CHAT_STACK },
			{ scope: "aichatLog", titleKey: "aichat.shortcut.scope.log", stack: AI_CHAT_STACK },
			{ scope: "diff", titleKey: "aichat.shortcut.scope.diff", stack: ["global", "diff"] },
		],
	},
	// The copy is a chunk: the router asks for it beside the session and registers it before the first
	// render, so no screen of the module ever draws a key without its text.
	i18n: () => import("./i18n/en.ts").then((chunk) => chunk.en),
	onBootstrap: onAichatBootstrap,
	hydrate: hydrateAichatSlice,
	dehydrate: (slice) => dehydrateAichatSlice(slice as AichatSlice),
}

export default aichat
